import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { GroupInfosManagement } from '../components/GroupInfosManagement';
import { useCreateGroup } from '@/hooks/group.hooks';
import { groupSchema, TGroupSchema } from '@/schemas/group/group.schema';
import { LUCIUS_GROUP_ID } from '@/api/entities/group';
import { getLanguage } from '@/utils/language.utils';
import { Loader } from '@/components/Loading/Loader';

export const CreateGroupPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('group');
  const {
    watch: watchGroup,
    control: groupControl,
    register: registerGroup,
    setValue: setGroupValue,
    setError: setGroupError,
    handleSubmit: handleSubmitGroup,
    formState: { errors: groupErrors, isSubmitting: isSubmittingGroup },
  } = useForm<TGroupSchema>({
    resolver: zodResolver(groupSchema),
  });

  const { mutate: mutateCreateGroup, isPending: isPendingCreateGroup } =
    useCreateGroup({
      t,
      handleSuccess: () => navigate('/groups'),
    });

  const onSubmitGroup = (group: TGroupSchema) => {
    const { languageCode } = getLanguage();

    const newGroup = {
      ...group,
      distributorId: group?.distributorId || LUCIUS_GROUP_ID,
      languageCode,
    };
    mutateCreateGroup(newGroup);
  };

  return (
    <>
      <PageHeader
        backToLink="/groups"
        title={t('common:group')}
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('groupList')} linkTo="/groups" />
            <BreadcrumbItem text={t('common:group')} active />
          </BreadcrumbList>
        }
      />
      <div className="flex flex-col gap-12">
        <GroupInfosManagement<TGroupSchema>
          t={t}
          watch={watchGroup}
          control={groupControl}
          register={registerGroup}
          setValue={setGroupValue}
          setError={setGroupError}
          errors={groupErrors}
          submitDisabled={isSubmittingGroup || isPendingCreateGroup}
          onSubmit={handleSubmitGroup(onSubmitGroup)}
        />
      </div>
      {(isSubmittingGroup || isPendingCreateGroup) && <Loader />}
    </>
  );
};
